// src/components/MonthView.jsx

import React from 'react';
import { SimpleGrid, Box, Center, Text, Tag, VStack, Tooltip } from '@chakra-ui/react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isBefore, startOfToday, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';

export default function MonthView({ settings, bookings, globalEvents, selectedDate, onDateClick }) {
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const daysOfWeek = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  return (
    <Box borderWidth="1px" borderRadius="lg" mt={6} p={4} bg="white">
      <SimpleGrid columns={7} spacing={1}>
        {daysOfWeek.map(day => (
          <Center key={day} fontWeight="bold" fontSize="sm" p={2}>{day}</Center>
        ))}
        {days.map(day => {
          const isCurrentMonth = isSameMonth(day, selectedDate);
          const dayOfWeek = day.getDay();
          const isToday = isSameDay(day, new Date());
          const isPast = isBefore(day, startOfToday());

          return (
            <Box
              key={day.toISOString()}
              minH="120px"
              borderWidth="1px"
              borderRadius="md"
              p={1}
              opacity={isPast ? 0.5 : (isCurrentMonth ? 1 : 0.4)}
              bg={isToday ? 'blue.50' : 'transparent'}
              onClick={isPast ? undefined : () => onDateClick(day)}
              cursor={isPast ? 'not-allowed' : 'pointer'}
              _hover={isPast ? {} : { bg: 'gray.100' }}
            >
              <Text fontWeight="bold" ml={1}>{format(day, 'd')}</Text>
              
              {isCurrentMonth && (
                <VStack align="stretch" spacing={1} mt={1}>
                  {settings?.resources?.map(resource => {
                    const { schedule = {}, specialEvents = [] } = resource;
                    
                    const resourceStartTimeStr = schedule.startTime || '07:00';
                    const resourceEndTimeStr = schedule.endTime || '22:00';
                    const resourceStartHour = parseInt(resourceStartTimeStr.split(':')[0]);
                    const resourceEndHour = parseInt(resourceEndTimeStr.split(':')[0]);

                    const operatingDays = schedule.operatingDays || [];
                    const isOperatingToday = operatingDays.length === 0 || operatingDays.includes(dayOfWeek);
                    
                    let totalSlots = 0;
                    if (isOperatingToday) {
                      totalSlots = resourceEndHour - resourceStartHour;
                    }
                    
                    if (totalSlots > 0) {
                      const bookingsForResource = bookings.filter(b => 
                        b.resourceId === resource.id &&
                        b.bookingTimestamp && 
                        isSameDay(b.bookingTimestamp.toDate(), day)
                      ).length;
                      
                      let globalEventHours = 0;
                      const globalEventsForDay = (globalEvents || []).filter(e => 
                        (e.assignedResourceIds || []).includes(resource.id) && 
                        (e.days || []).includes(dayOfWeek)
                      );
                      globalEventsForDay.forEach(event => {
                        const startHour = parseInt(event.startTime.split(':')[0]);
                        const endHour = parseInt(event.endTime.split(':')[0]);
                        globalEventHours += (endHour - startHour);
                      });

                      const specialEvent = (specialEvents || []).find(e => {
                        const eventStart = startOfDay(new Date(e.startDate));
                        const eventEnd = endOfDay(new Date(e.endDate));
                        return startOfDay(day) >= eventStart && startOfDay(day) <= eventEnd;
                      });
                      
                      let specialEventHours = 0;
                      if (specialEvent) {
                        if (specialEvent.type === 'vacation') {
                          specialEventHours = totalSlots;
                        } else {
                          const startHour = parseInt(specialEvent.startTime.split(':')[0]);
                          const endHour = parseInt(specialEvent.endTime.split(':')[0]);
                          specialEventHours = endHour - startHour;
                        }
                      }

                      const occupiedSlots = bookingsForResource + globalEventHours + specialEventHours;
                      
                      let color = 'green';
                      if (occupiedSlots > 0 && occupiedSlots < totalSlots) {
                        color = 'orange';
                      } else if (occupiedSlots >= totalSlots) {
                        color = 'red';
                      }

                      return (
                        <Tooltip key={resource.id} label={`${occupiedSlots} de ${totalSlots} horarios ocupados`}>
                          <Tag size="sm" colorScheme={color} w="full" isTruncated>
                            {resource.name}
                          </Tag>
                        </Tooltip>
                      );
                    }
                    return null;
                  })}
                </VStack>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
